import {
  PageNotFound,
  QuestionnaireQuestions,
} from '@getpopsure/qnr-framework';
import { ErrorWithAction } from 'components/ErrorWithAction';
import LoadingSpinner from 'components/loadingSpinner';
import { useEffect, useState } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { useParams } from 'react-router-dom';
import { useSafeTranslation } from 'shared/i18n';
import { isStagingOrDev } from 'shared/util/isStagingOrDev';

import { useLoadQuestionnaire } from '../functions';
import { AdjustPolicyQuestionnaire } from './AdjustPolicyQuestionnaire';
import { useFetchPolicyAndEntities } from './hooks/useFetchPolicyAndEntities';
import { useAdjustPolicyStorage } from './storage';

export const AdjustPolicyFlow = () => {
  const { verticalId, policyId } = useParams<{
    verticalId: string;
    policyId: string;
  }>();
  const { t } = useSafeTranslation();

  const {
    policy,
    policyQuestionnaire,
    policyQuote,
    error: entityFetchError,
    isInitialLoading,
  } = useFetchPolicyAndEntities({ policyId });

  const [answers, setAnswers] = useState<QuestionnaireQuestions>({});

  const { removeQuestionnaireAnswers } = useAdjustPolicyStorage(verticalId);

  const [errorMessage, setErrorMessage] = useState<string | undefined>();

  const json = useLoadQuestionnaire(verticalId, false, false, true);

  useEffect(() => {
    if (entityFetchError?.message) {
      setErrorMessage(entityFetchError?.message);
    }
  }, [entityFetchError?.message]);

  if (json === undefined || isInitialLoading) {
    return <LoadingSpinner />;
  }

  if (!json) {
    return <PageNotFound />;
  }

  const data = json(t, { answers, policy });

  if (!data || (!isStagingOrDev && !data.meta.published)) {
    return <PageNotFound />;
  }

  if (errorMessage || !policy || !policyQuestionnaire || !policyQuote) {
    return (
      <ErrorWithAction
        title="Questionnaire error"
        description={errorMessage ?? ''}
      />
    );
  }

  const resetData = () => {
    removeQuestionnaireAnswers();
    window.location.reload();
  };

  return (
    <>
      <ErrorBoundary
        fallbackRender={() => (
          <ErrorWithAction
            title="Questionnaire data error"
            description="The questionnaire answers do not match the questions. Some questions have changed"
            cta={{
              title: 'Reset data',
              onClick: resetData,
            }}
          />
        )}
      >
        <AdjustPolicyQuestionnaire
          data={data}
          verticalId={verticalId}
          onQuestionnaireAnswersChanged={setAnswers}
          policy={policy}
          policyQuestionnaire={policyQuestionnaire}
          policyQuote={policyQuote}
        />
      </ErrorBoundary>
    </>
  );
};
